import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ResendConfirmationCodeCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  ChangePasswordCommand,
  GetUserCommand,
  GlobalSignOutCommand,
  UpdateUserAttributesCommand,
  VerifyUserAttributeCommand,
  UserNotConfirmedException,
  CodeMismatchException,
  ExpiredCodeException,
  LimitExceededException,
  NotAuthorizedException,
  UserNotFoundException,
  InvalidPasswordException,
  InvalidParameterException,
  TooManyRequestsException,
  CodeDeliveryFailureException,
  UserLambdaValidationException,
  UsernameExistsException,
  InitiateAuthCommandOutput,
  AuthenticationResultType,
  AdminConfirmSignUpCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { COGNITO_CONFIG } from '@/lib/config/cognito.config';
import { AuthenticationError } from '@/lib/errors/auth.errors';

export interface CognitoSignInResult {
  userId: string;
  email: string;
  name: string | null;
  accessToken: string;
  idToken: string;
  refreshToken: string;
  hasFilledQuestionnaire: boolean;
  formAnswerId: string;
  status: 'CONFIRMED' | 'UNCONFIRMED';
  role: string;
}

export interface CognitoUserAttributes {
  email?: string;
  phone_number?: string;
  name?: string;
  [key: string]: string | undefined;
}

export class CognitoError extends Error {
  constructor(
    message: string,
    public code: string
  ) {
    super(message);
    this.name = 'CognitoError';
  }
}

export class CognitoService {
  private client: CognitoIdentityProviderClient;
  private readonly region: string;
  private readonly userPoolId: string;
  private readonly clientId: string;
  private readonly enableLogging: boolean;

  constructor() {
    // Validate configuration
    if (!COGNITO_CONFIG.REGION) {
      throw new Error('AWS Region is not configured');
    }
    if (!COGNITO_CONFIG.USER_POOL_ID) {
      throw new Error('Cognito User Pool ID is not configured');
    }
    if (!COGNITO_CONFIG.CLIENT_ID) {
      throw new Error('Cognito Client ID is not configured');
    }

    this.region = COGNITO_CONFIG.REGION;
    this.userPoolId = COGNITO_CONFIG.USER_POOL_ID;
    this.clientId = COGNITO_CONFIG.CLIENT_ID;
    this.enableLogging = process.env.NODE_ENV !== 'production';

    this.client = new CognitoIdentityProviderClient({
      region: this.region,
    });
  }

  private handleCognitoError(error: any): never {
    if (this.enableLogging) {
      console.error('Cognito error:', error);
    }

    if (error instanceof NotAuthorizedException) {
      throw new CognitoError('Invalid credentials', 'NotAuthorizedException');
    }
    if (error instanceof UserNotFoundException) {
      throw new CognitoError('User not found', 'UserNotFoundException');
    }
    if (error instanceof InvalidPasswordException) {
      throw new CognitoError(
        'Invalid password format',
        'InvalidPasswordException'
      );
    }
    if (error instanceof CodeMismatchException) {
      throw new CognitoError(
        'Invalid verification code',
        'CodeMismatchException'
      );
    }
    if (error instanceof ExpiredCodeException) {
      throw new CognitoError(
        'Verification code has expired',
        'ExpiredCodeException'
      );
    }
    if (error instanceof LimitExceededException) {
      throw new CognitoError(
        'Too many attempts, please try again later',
        'LimitExceededException'
      );
    }
    if (error instanceof TooManyRequestsException) {
      throw new CognitoError(
        'Too many requests, please try again later',
        'TooManyRequestsException'
      );
    }
    if (error instanceof InvalidParameterException) {
      throw new CognitoError(
        'Invalid parameter provided',
        'InvalidParameterException'
      );
    }

    throw new CognitoError(
      error.message || 'An unexpected error occurred',
      error.name || 'UnknownError'
    );
  }

  async signUp(
    email: string,
    password: string,
    attributes?: CognitoUserAttributes
  ): Promise<any> {
    if (this.enableLogging) {
      console.log('Attempting sign up for:', { email });
    }
    try {
      // Filter out undefined values and format attributes properly
      const userAttributes = [
        { Name: 'email', Value: email },
        ...(attributes?.name ? [{ Name: 'name', Value: attributes.name }] : []),
        ...(attributes?.picture
          ? [{ Name: 'picture', Value: attributes.picture }]
          : []),
        ...(attributes?.phone_number
          ? [{ Name: 'phone_number', Value: attributes.phone_number }]
          : []),
      ];

      const command = new SignUpCommand({
        ClientId: this.clientId,
        Username: email,
        Password: password,
        UserAttributes: userAttributes,
      });

      const response = await this.client.send(command);
      if (this.enableLogging) {
        console.log('Sign up successful:', { email });
      }

      // For Google users, we'll skip the confirmation step since email is already verified
      if (attributes?.picture && this.enableLogging) {
        console.log('Google user signed up, skipping confirmation:', { email });
      }

      return response;
    } catch (unknownError) {
      const error = unknownError as Error;

      if (error instanceof UsernameExistsException) {
        throw new CognitoError(
          'User already exists',
          'UsernameExistsException'
        );
      }
      if (error instanceof InvalidPasswordException) {
        throw new CognitoError(
          'Invalid password format',
          'InvalidPasswordException'
        );
      }
      if (error instanceof InvalidParameterException) {
        if (this.enableLogging) {
          console.error('Invalid parameter error:', error);
        }
        throw new CognitoError(
          'Invalid parameter provided',
          'InvalidParameterException'
        );
      }
      if (error instanceof CodeDeliveryFailureException) {
        throw new CognitoError(
          'Failed to deliver confirmation code',
          'CodeDeliveryFailureException'
        );
      }
      if (error instanceof UserLambdaValidationException) {
        throw new CognitoError(
          'User validation failed',
          'UserLambdaValidationException'
        );
      }

      if (this.enableLogging) {
        console.error('Unknown error during sign up:', error);
      }
      throw new CognitoError(
        error.message || 'An unexpected error occurred',
        error.name || 'UnknownError'
      );
    }
  }

  async confirmSignUp(email: string, code: string): Promise<void> {
    if (this.enableLogging) {
      console.log('Attempting to confirm sign up for:', { email, code });
    }
    try {
      // For Google users, we skip confirmation since email is already verified
      if (code === 'AUTO_CONFIRM') {
        if (this.enableLogging) {
          console.log('Skipping confirmation for Google user:', { email });
        }
        return;
      }

      const command = new ConfirmSignUpCommand({
        ClientId: this.clientId,
        Username: email,
        ConfirmationCode: code,
      });

      await this.client.send(command);
      if (this.enableLogging) {
        console.log('Confirmation successful:', { email });
      }
    } catch (unknownError) {
      const error = unknownError as Error;

      if (error instanceof CodeMismatchException) {
        throw new CognitoError(
          'Invalid verification code',
          'CodeMismatchException'
        );
      }
      if (error instanceof ExpiredCodeException) {
        throw new CognitoError(
          'Verification code has expired',
          'ExpiredCodeException'
        );
      }
      if (error instanceof UserNotFoundException) {
        throw new CognitoError('User not found', 'UserNotFoundException');
      }
      if (error instanceof InvalidParameterException) {
        throw new CognitoError(
          'Invalid parameter provided',
          'InvalidParameterException'
        );
      }
      if (error instanceof TooManyRequestsException) {
        throw new CognitoError(
          'Too many requests, please try again later',
          'TooManyRequestsException'
        );
      }
      if (error instanceof NotAuthorizedException) {
        throw new CognitoError(
          'Not authorized to perform this action',
          'NotAuthorizedException'
        );
      }

      throw new CognitoError(
        error.message || 'An unexpected error occurred',
        error.name || 'UnknownError'
      );
    }
  }

  async resendConfirmationCode(email: string): Promise<void> {
    if (this.enableLogging) {
      console.log('Attempting to resend confirmation code for:', { email });
    }
    try {
      const command = new ResendConfirmationCodeCommand({
        ClientId: this.clientId,
        Username: email,
      });

      await this.client.send(command);
      if (this.enableLogging) {
        console.log('Confirmation code resent successfully:', { email });
      }
    } catch (error) {
      this.handleCognitoError(error);
    }
  }

  async signIn(
    email: string,
    password: string,
    isGoogleAuth: boolean = false
  ): Promise<CognitoSignInResult> {
    if (this.enableLogging) {
      console.log('Attempting sign in for:', { email });
    }

    try {
      const command = new InitiateAuthCommand({
        ClientId: this.clientId,
        AuthFlow: 'USER_PASSWORD_AUTH',
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password,
        },
      });

      if (this.enableLogging) {
        console.log('Sending auth command with ClientId:', this.clientId);
      }
      const result = await this.client.send(command);
      if (this.enableLogging) {
        console.log('Sign in successful:', { email });
      }

      if (!result.AuthenticationResult?.AccessToken) {
        throw new CognitoError('No access token received', 'NoAccessToken');
      }

      const accessToken = result.AuthenticationResult.AccessToken;
      const idToken = result.AuthenticationResult.IdToken;
      const refreshToken = result.AuthenticationResult.RefreshToken;

      if (!accessToken || !idToken) {
        throw new CognitoError('Invalid tokens received', 'InvalidTokens');
      }

      const decodedAccessToken = JSON.parse(
        Buffer.from(accessToken.split('.')[1], 'base64').toString()
      );

      const decodedIdToken = JSON.parse(
        Buffer.from(idToken.split('.')[1], 'base64').toString()
      );

      return {
        userId: decodedAccessToken.sub,
        email,
        name: decodedIdToken.name || null,
        accessToken,
        idToken,
        refreshToken: refreshToken || '',
        hasFilledQuestionnaire: false,
        formAnswerId: '',
        status: 'CONFIRMED',
        role: 'user',
      };
    } catch (unknownError) {
      const error = unknownError as Error;

      if (error instanceof UserNotConfirmedException) {
        if (this.enableLogging) {
          console.log('User not confirmed:', { email });
        }
        return {
          userId: email,
          email,
          name: null,
          accessToken: '',
          idToken: '',
          refreshToken: '',
          hasFilledQuestionnaire: false,
          formAnswerId: '',
          status: 'UNCONFIRMED',
          role: 'user',
        };
      }

      if (error instanceof NotAuthorizedException) {
        throw new CognitoError('Invalid credentials', 'NotAuthorizedException');
      }
      if (error instanceof UserNotFoundException) {
        throw new CognitoError('User not found', 'UserNotFoundException');
      }
      if (error instanceof InvalidPasswordException) {
        throw new CognitoError(
          'Invalid password format',
          'InvalidPasswordException'
        );
      }
      if (error instanceof LimitExceededException) {
        throw new CognitoError(
          'Too many attempts, please try again later',
          'LimitExceededException'
        );
      }
      if (error instanceof TooManyRequestsException) {
        throw new CognitoError(
          'Too many requests, please try again later',
          'TooManyRequestsException'
        );
      }
      if (error instanceof InvalidParameterException) {
        throw new CognitoError(
          'Invalid parameter provided',
          'InvalidParameterException'
        );
      }

      throw new CognitoError(
        error.message || 'An unexpected error occurred',
        error.name || 'UnknownError'
      );
    }
  }

  async refreshToken(
    refreshTokenStr: string
  ): Promise<CognitoSignInResult | null> {
    try {
      const command: InitiateAuthCommand = new InitiateAuthCommand({
        ClientId: this.clientId,
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        AuthParameters: {
          REFRESH_TOKEN: refreshTokenStr,
          SCOPE: 'aws.cognito.signin.user.admin',
        },
      });

      const result: InitiateAuthCommandOutput = await this.client.send(command);
      const authResult: AuthenticationResultType | undefined =
        result.AuthenticationResult;

      if (!authResult?.AccessToken) {
        return null;
      }

      const accessToken = authResult.AccessToken;
      const idToken = authResult.IdToken;
      const newRefreshToken = authResult.RefreshToken;
      const decodedAccessToken = JSON.parse(
        Buffer.from(accessToken.split('.')[1], 'base64').toString()
      );

      return {
        userId: decodedAccessToken.sub,
        email: '',
        name: null,
        accessToken: accessToken,
        idToken: idToken || '',
        refreshToken: newRefreshToken || refreshTokenStr,
        hasFilledQuestionnaire: false,
        formAnswerId: '',
        status: 'CONFIRMED',
        role: 'user',
      };
    } catch (error) {
      this.handleCognitoError(error);
    }
  }

  async forgotPassword(email: string): Promise<void> {
    if (this.enableLogging) {
      console.log('Initiating forgot password for:', { email });
    }
    try {
      const command = new ForgotPasswordCommand({
        ClientId: this.clientId,
        Username: email,
      });

      await this.client.send(command);
      if (this.enableLogging) {
        console.log('Forgot password code sent successfully:', { email });
      }
    } catch (error) {
      this.handleCognitoError(error);
    }
  }

  async confirmForgotPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<void> {
    if (this.enableLogging) {
      console.log('Confirming forgot password for:', { email });
    }
    try {
      const command = new ConfirmForgotPasswordCommand({
        ClientId: this.clientId,
        Username: email,
        ConfirmationCode: code,
        Password: newPassword,
      });

      await this.client.send(command);
      if (this.enableLogging) {
        console.log('Password reset successful:', { email });
      }
    } catch (error) {
      this.handleCognitoError(error);
    }
  }

  async changePassword(
    accessToken: string,
    oldPassword: string,
    newPassword: string
  ): Promise<void> {
    if (this.enableLogging) {
      console.log('Changing password');
    }
    try {
      const command = new ChangePasswordCommand({
        AccessToken: accessToken,
        PreviousPassword: oldPassword,
        ProposedPassword: newPassword,
      });

      await this.client.send(command);
      if (this.enableLogging) {
        console.log('Password changed successfully');
      }
    } catch (error) {
      this.handleCognitoError(error);
    }
  }

  async updateUserAttributes(
    accessToken: string,
    attributes: CognitoUserAttributes
  ): Promise<void> {
    if (this.enableLogging) {
      console.log('Updating user attributes');
    }
    try {
      const userAttributes = Object.entries(attributes).map(([key, value]) => ({
        Name: key,
        Value: value as string,
      }));

      const command = new UpdateUserAttributesCommand({
        AccessToken: accessToken,
        UserAttributes: userAttributes,
      });

      await this.client.send(command);
      if (this.enableLogging) {
        console.log('User attributes updated successfully');
      }
    } catch (error) {
      this.handleCognitoError(error);
    }
  }

  async verifyUserAttribute(
    accessToken: string,
    attributeName: string,
    code: string
  ): Promise<void> {
    if (this.enableLogging) {
      console.log('Verifying user attribute:', { attributeName });
    }
    try {
      const command = new VerifyUserAttributeCommand({
        AccessToken: accessToken,
        AttributeName: attributeName,
        Code: code,
      });

      await this.client.send(command);
      if (this.enableLogging) {
        console.log('Attribute verified successfully:', { attributeName });
      }
    } catch (error) {
      this.handleCognitoError(error);
    }
  }

  async getUserAttributes(accessToken: string): Promise<CognitoUserAttributes> {
    if (this.enableLogging) {
      console.log('Getting user attributes');
    }
    try {
      const command = new GetUserCommand({
        AccessToken: accessToken,
      });

      const response = await this.client.send(command);
      const attributes: CognitoUserAttributes = {};

      response.UserAttributes?.forEach((attr) => {
        if (attr.Name && attr.Value) {
          attributes[attr.Name] = attr.Value;
        }
      });

      return attributes;
    } catch (error) {
      this.handleCognitoError(error);
    }
  }

  async signOut(accessToken: string): Promise<void> {
    if (this.enableLogging) {
      console.log('Signing out user');
    }
    try {
      const command = new GlobalSignOutCommand({
        AccessToken: accessToken,
      });

      await this.client.send(command);
      if (this.enableLogging) {
        console.log('Sign out successful');
      }
    } catch (error) {
      this.handleCognitoError(error);
    }
  }

  async signInWithGoogle(
    email: string,
    googleToken: string
  ): Promise<CognitoSignInResult> {
    if (this.enableLogging) {
      console.log('Attempting Google sign in for:', { email });
    }
    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'CUSTOM_AUTH',
        ClientId: this.clientId,
        AuthParameters: {
          USERNAME: email,
          CUSTOM_CHALLENGE: googleToken,
        },
      });

      const response = await this.client.send(command);
      const authResult = response.AuthenticationResult;

      if (!authResult?.AccessToken || !authResult?.IdToken) {
        throw new Error('Invalid authentication result');
      }

      // Decode the ID token to get user information
      const idTokenPayload = JSON.parse(
        Buffer.from(authResult.IdToken.split('.')[1], 'base64').toString()
      );

      return {
        userId: idTokenPayload.sub,
        email: idTokenPayload.email,
        name: idTokenPayload.name || null,
        accessToken: authResult.AccessToken,
        idToken: authResult.IdToken,
        refreshToken: authResult.RefreshToken || '',
        hasFilledQuestionnaire: false,
        formAnswerId: '',
        status: 'CONFIRMED',
        role: 'user',
      };
    } catch (error) {
      if (this.enableLogging) {
        console.error('Google sign in error:', error);
      }
      this.handleCognitoError(error);
    }
  }
}

export const cognitoService = new CognitoService();
